// frontend/src/components/StatusUpdateDialog.js
import React, { useState, useEffect } from 'react';
import {
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  Box,
  Typography,
  Stack,
  Chip,
} from '@mui/material';
import { toast } from 'react-toastify';
import useAuth from '../hooks/useAuth';
import PersonIcon from "@mui/icons-material/Person";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CloseIcon from "@mui/icons-material/Close";
import { alpha, useTheme } from '@mui/material/styles';
import CrmDialog from "../components/CrmDialog";
import {
  LEAD_STATUS_DISPOSITION_OPTIONS,
  getLeadStatusColor,
} from "../constants/leadStatuses";
import { formatStateAbbr } from "../constants/usStates";
import LeadStatusIcon from "./LeadStatusIcon";

// ─── Visual style constants ───────────────────────────────────────────────────

const primaryBlueButtonSx = {
  borderRadius: "14px",
  minHeight: 42,
  textTransform: "none",
  fontWeight: 800,
  fontSize: "0.9rem",
  letterSpacing: "-0.01em",
  background: "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
  color: "#fff",
  boxShadow: "0 10px 22px rgba(37,99,235,0.28)",
  transition: "all 0.18s ease",
  "&:hover": {
    background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
    boxShadow: "0 14px 28px rgba(37,99,235,0.38)",
    transform: "translateY(-1px)",
  },
};

const secondaryButtonSx = {
  borderRadius: "14px",
  minHeight: 42,
  textTransform: "none",
  fontWeight: 700,
  px: 2,
  border: "1px solid rgba(15,23,42,0.08)",
  background: "rgba(255,255,255,0.72)",
  color: "#1e293b",
  "&:hover": {
    background: "rgba(255,255,255,0.94)",
  },
};

const sectionLabelSx = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "text.secondary",
  mb: 0.75,
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "14px",
    background: "rgba(248,250,252,0.95)",
  },
};

const glassCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(15,23,42,0.08)",
  background: "rgba(255,255,255,0.86)",
  backdropFilter: "blur(12px)",
  boxShadow: "0 10px 28px rgba(15,23,42,0.05)",
};

// ─────────────────────────────────────────────────────────────────────────────

export default function StatusUpdateDialog({
  open,
  onClose,
  lead,
  currentStatus,
  onSave,
  initiallyVisited = false,
  hideVisitOptions = false,
}) {
  const { isReadOnly } = useAuth();
  const theme = useTheme();
  const [selectedStatus, setSelectedStatus] = useState(currentStatus || '');
  const [notes, setNotes] = useState('');
  const [markVisited, setMarkVisited] = useState(initiallyVisited);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const [navigateToNext, setNavigateToNext] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [editingName, setEditingName] = useState(false);

  // NEW: follow-up date state (used only when "Follow Up Needed" is selected)
  const [followUpDate, setFollowUpDate] = useState('');

  useEffect(() => {
    if (open && lead) {
      setSelectedStatus(lead.status || currentStatus || '');
      setNotes(lead.notes || '');
      setMarkVisited(true);

      // ✅ Find incomplete follow-up and use its due_date if exists
      const incompleteFollowUp = lead.followups?.find(f => !f.completed);
      if (incompleteFollowUp?.due_date) {
        const dueDate = new Date(incompleteFollowUp.due_date);
        const formattedDate = dueDate.toISOString().split('T')[0];
        setFollowUpDate(formattedDate);
      } else {
        setFollowUpDate('');
      }

      setFirstName(lead.first_name || '');
      setLastName(lead.last_name || '');
      setEditingName(false);
    }
  }, [open, lead, currentStatus, initiallyVisited]);

  const handleSave = () => {
    if (isReadOnly) return;

    if (!selectedStatus) {
      toast.error('Please select a disposition');
      return;
    }

    if (selectedStatus === 'Follow Up Needed' && !followUpDate) {
      toast.error('Please select a follow-up date');
      return;
    }

    onSave({
      status: selectedStatus,
      notes: notes.trim(),
      markVisited,
      autoAdvance,
      follow_up_date: selectedStatus === 'Follow Up Needed' ? followUpDate : null,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
    });

    // Reset form
    setNotes('');
    setMarkVisited(initiallyVisited);
    setAutoAdvance(true);
    setNavigateToNext(false);
    setFollowUpDate('');
  };

  const handleClose = () => {
    setSelectedStatus(currentStatus || '');
    setNotes('');
    setMarkVisited(initiallyVisited);
    setAutoAdvance(true);
    setNavigateToNext(false);
    setFollowUpDate('');
    onClose();
  };

  if (!lead) return null;

  return (
    <CrmDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "20px",
          overflow: "hidden",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(247,249,252,0.98) 100%)",
          boxShadow: "0 18px 50px rgba(15,23,42,0.20)",
          border: "1px solid rgba(15,23,42,0.08)",
        },
      }}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 2.25,
          pt: 1.8,
          pb: 1.4,
          borderBottom: "1px solid rgba(15,23,42,0.08)",
          background: "rgba(255,255,255,0.72)",
          backdropFilter: "blur(14px)",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
          <Box>
            <Typography
              sx={{
                fontSize: 22,
                fontWeight: 800,
                lineHeight: 1.08,
                letterSpacing: "-0.02em",
              }}
            >
              Update Lead Status
            </Typography>
            <Typography sx={{ mt: 0.35, fontSize: 13, color: "text.secondary" }}>
              Select disposition and update notes
            </Typography>
          </Box>

          <Button
            size="small"
            onClick={handleClose}
            sx={{
              minWidth: 36,
              width: 36,
              height: 36,
              p: 0,
              borderRadius: "12px",
              border: "1px solid rgba(15,23,42,0.08)",
              color: "text.secondary",
            }}
          >
            <CloseIcon fontSize="small" />
          </Button>
        </Box>
      </Box>

      {/* ── Content ────────────────────────────────────────────────────────── */}
      <DialogContent sx={{ p: 2.25 }}>

        {/* Lead identity card */}
        <Box sx={{ ...glassCardSx, p: 1.6, mb: 2 }}>
          <Stack spacing={1.05}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
                <PersonIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                {!editingName ? (
                  <Typography
                    sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.1, cursor: "pointer" }}
                    onClick={() => setEditingName(true)}
                    title="Click to edit name"
                  >
                    {firstName || "First"} {lastName || "Last"}
                  </Typography>
                ) : (
                  <Stack direction="row" spacing={0.75}>
                    <TextField
                      size="small"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      placeholder="First"
                      autoFocus
                      sx={fieldSx}
                    />
                    <TextField
                      size="small"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      placeholder="Last"
                      onBlur={() => setEditingName(false)}
                      sx={fieldSx}
                    />
                  </Stack>
                )}
              </Stack>

              <Chip
                label={`Current: ${currentStatus || lead?.status || "New"}`}
                size="small"
                sx={{
                  borderRadius: "999px",
                  fontWeight: 700,
                  background: "rgba(37,99,235,0.08)",
                  color: "#2563eb",
                  flexShrink: 0,
                }}
              />
            </Stack>

            <Stack direction="row" spacing={0.8} alignItems="flex-start">
              <LocationOnIcon sx={{ fontSize: 17, color: "text.secondary", mt: 0.1 }} />
              <Typography variant="body2" color="text.secondary">
                {[lead?.address, lead?.city, formatStateAbbr(lead?.state), lead?.zip_code]
                  .filter(Boolean)
                  .join(", ") || "No address available"}
              </Typography>
            </Stack>
          </Stack>
        </Box>

        {/* Disposition grid */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={sectionLabelSx}>Select Disposition</Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1,
            }}
          >
            {LEAD_STATUS_DISPOSITION_OPTIONS.map((opt) => {
              const active = selectedStatus === opt.value;
              const color = getLeadStatusColor(opt.value);

              return (
                <Box
                  key={opt.value}
                  onClick={() => setSelectedStatus(opt.value)}
                  sx={{
                    cursor: "pointer",
                    borderRadius: "14px",
                    border: `1px solid ${active ? alpha(color, 0.35) : "rgba(15,23,42,0.08)"}`,
                    background: active
                      ? alpha(color, 0.08)
                      : "rgba(255,255,255,0.75)",
                    px: 1.2,
                    py: 1.1,
                    transition: "all 0.18s ease",
                    boxShadow: active ? `0 0 0 1px ${alpha(color, 0.10)}` : "none",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      background: active
                        ? alpha(color, 0.10)
                        : "rgba(255,255,255,0.95)",
                    },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LeadStatusIcon status={opt.value} size={20} color={color} />
                    <Typography sx={{ fontSize: 13.5, fontWeight: active ? 800 : 700 }}>
                      {opt.value}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Follow-Up Date – conditional */}
        {selectedStatus === "Follow Up Needed" && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={sectionLabelSx}>Follow-Up Date</Typography>
            <TextField
              type="date"
              size="small"
              value={followUpDate}
              onChange={(e) => setFollowUpDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ ...fieldSx, maxWidth: 220 }}
            />
          </Box>
        )}

        {/* Notes */}
        <Box sx={{ mb: 2 }}>
          <Typography sx={sectionLabelSx}>Notes</Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes about this visit..."
            sx={fieldSx}
          />
        </Box>

        {/* Visit options */}
        {!hideVisitOptions && (
          <Box
            sx={{
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.08)",
              background: "rgba(255,255,255,0.72)",
              p: 1.25,
              mb: 1,
            }}
          >
            <Typography sx={sectionLabelSx}>Options</Typography>
            <Stack spacing={0.9}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Checkbox
                  size="small"
                  checked={markVisited}
                  onChange={(e) => setMarkVisited(e.target.checked)}
                />
                <Typography variant="body2">Mark as Visited</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Checkbox
                  size="small"
                  checked={autoAdvance}
                  onChange={(e) => setAutoAdvance(e.target.checked)}
                />
                <Typography variant="body2">Auto-advance to Next Stop</Typography>
              </Box>
            </Stack>
          </Box>
        )}
      </DialogContent>

      {/* ── Footer ─────────────────────────────────────────────────────────── */}
      <DialogActions
        sx={{
          px: 2.25,
          py: 1.6,
          borderTop: "1px solid rgba(15,23,42,0.08)",
          background: "rgba(255,255,255,0.72)",
          backdropFilter: "blur(14px)",
          gap: 1,
        }}
      >
        <Button onClick={handleClose} sx={secondaryButtonSx}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={isReadOnly || !selectedStatus}
          sx={{
            ...primaryBlueButtonSx,
            opacity: isReadOnly ? 0.5 : 1,
          }}
        >
          Save & Continue
        </Button>
      </DialogActions>
    </CrmDialog>
  );
}